import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import { useTranslation } from "react-i18next";
import { getMyStores } from "../../api/storeService";
import { StoreTypeLabels, StoreTypeColors } from "../Admin/constants";
import { EmptyState } from "../Admin/analyticsShared";

export default function Stores() {
  const { t } = useTranslation(["boStores", "boSidebar", "boShared"]);
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getMyStores()
      .then((data) => {
        if (active) setStores(data);
      })
      .catch(() => {
        if (active) setError(t("boStores:loadError"));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        {t("boSidebar:myStores")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("boStores:storesRegistered", { count: stores.length })}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : stores.length === 0 ? (
        <EmptyState icon={StorefrontRoundedIcon} text={t("boStores:noStores")} />
      ) : (
        <Grid container spacing={2}>
          {stores.map((store) => (
            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={store.storeId}>
              <Card
                sx={{
                  p: 2.5,
                  height: "100%",
                  cursor: "pointer",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 14px 28px rgba(15, 23, 42, 0.12)",
                  },
                }}
                onClick={() => navigate(`/business-owner/stores/${store.storeId}`)}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Avatar src={store.logoUrl || undefined} variant="rounded" sx={{ width: 48, height: 48 }}>
                    <StorefrontRoundedIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700} noWrap>
                      {store.storeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                      {store.cityNameEnglish || store.city || t("boShared:noCitySet")}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5, rowGap: 1 }}>
                  <Chip
                    size="small"
                    label={StoreTypeLabels[store.type] || t("boShared:storeFallback")}
                    color={StoreTypeColors[store.type] || "default"}
                  />
                  <Chip
                    size="small"
                    label={store.isActive ? t("boShared:active") : t("boShared:inactive")}
                    color={store.isActive ? "success" : "default"}
                    variant="outlined"
                  />
                  {!store.isApproved && <Chip size="small" label={t("boShared:pendingApproval")} color="warning" variant="outlined" />}
                  {store.isComingSoon && <Chip size="small" label={t("boShared:comingSoon")} color="info" variant="outlined" />}
                </Stack>

                <Stack direction="row" spacing={2.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <StarRoundedIcon sx={{ fontSize: 18, color: "warning.main" }} />
                    <Typography variant="body2" fontWeight={600}>
                      {store.rating > 0 ? store.rating.toFixed(1) : "-"}
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <PeopleAltOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                    <Typography variant="body2" fontWeight={600}>
                      {store.followersCount ?? 0}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ ml: "auto", color: "primary.main", cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/business-owner/dashboard?storeId=${store.storeId}`);
                    }}
                  >
                    <QueryStatsRoundedIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2" fontWeight={600}>
                      {t("boShared:analytics")}
                    </Typography>
                  </Stack>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
